import React, { useState } from 'react';
import { ChevronDown, Award } from 'lucide-react';
import { servicesPageExtras } from '../mock';
import { FAQ_CHEVRON_SIZE, TRUST_BADGE_ICON_SIZE } from '../constants/icon-sizes';

const IntroSection = () => (
  <section className="py-24 bg-slate-50">
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
      <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
        About Our Services
      </span>
      <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-8 leading-tight">
        {servicesPageExtras.intro.title.replace('Melbourne', '').trim()}{' '}
        <span className="text-red-600">Melbourne</span> Homes & Businesses
      </h2>
      <div className="space-y-5 text-lg text-slate-700 leading-relaxed text-left">
        {servicesPageExtras.intro.paragraphs.map((p) => (
          <p key={p.slice(0, 40)}>{p}</p>
        ))}
      </div>
    </div>
  </section>
);

const WhyChooseUsCard = ({ item, idx }) => (
  <div
    data-testid={`services-why-${idx}`}
    className="bg-slate-50 rounded-2xl p-7 border border-slate-200 hover:border-blue-200 hover:shadow-md transition-all duration-300 text-center"
  >
    <div className="text-4xl md:text-5xl font-bold text-blue-600 mb-2">{item.stat}</div>
    <div className="text-lg font-bold text-slate-900 mb-3">{item.label}</div>
    <p className="text-slate-600 text-sm leading-relaxed">{item.description}</p>
  </div>
);

const WhyChooseUsSection = () => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-14">
        <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
          Why Choose Us
        </span>
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
          The AA Plumbing & Roofing <span className="text-red-600">Difference</span>
        </h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {servicesPageExtras.whyChooseUs.map((item, idx) => (
          <WhyChooseUsCard key={item.label} item={item} idx={idx} />
        ))}
      </div>
    </div>
  </section>
);

const FaqItem = ({ faq, idx, open, onToggle }) => (
  <div
    data-testid={`services-faq-${idx}`}
    className="bg-white rounded-xl border border-slate-200 overflow-hidden transition-all duration-300"
  >
    <button
      onClick={onToggle}
      className="w-full text-left px-6 py-5 flex items-center justify-between gap-4 hover:bg-slate-50 transition-colors duration-200"
    >
      <span className="font-semibold text-slate-900 text-base md:text-lg">{faq.q}</span>
      <ChevronDown
        size={FAQ_CHEVRON_SIZE}
        className={`text-blue-600 flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}
      />
    </button>
    {open && (
      <div className="px-6 pb-5 text-slate-600 leading-relaxed border-t border-slate-100 pt-4">
        {faq.a}
      </div>
    )}
  </div>
);

const FaqSection = () => {
  const [openFaq, setOpenFaq] = useState(0);
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <span className="inline-block text-blue-600 font-semibold text-sm uppercase tracking-wider mb-3">
            Frequently Asked
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900">
            Questions & <span className="text-red-600">Answers</span>
          </h2>
        </div>
        <div className="space-y-3">
          {servicesPageExtras.faqs.map((faq, idx) => (
            <FaqItem
              key={faq.q}
              faq={faq}
              idx={idx}
              open={openFaq === idx}
              onToggle={() => setOpenFaq(openFaq === idx ? -1 : idx)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

const TrustBadge = () => (
  <section className="py-16 bg-slate-900">
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex flex-col md:flex-row items-center justify-center gap-6 text-center md:text-left">
        <div className="bg-blue-600 p-4 rounded-2xl shadow-lg">
          <Award className="text-white" size={TRUST_BADGE_ICON_SIZE} />
        </div>
        <div>
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2">
            Fully Licensed, Insured & Compliant
          </h3>
          <p className="text-slate-300 text-base md:text-lg">
            BPC licensed • WorkSafe Victoria certified • Colorbond & BlueScope authorised • Full public liability insurance on every job
          </p>
        </div>
      </div>
    </div>
  </section>
);

const ServicesDeepDive = () => (
  <>
    <IntroSection />
    <WhyChooseUsSection />
    <FaqSection />
    <TrustBadge />
  </>
);

export default ServicesDeepDive;
